import { useEffect } from 'react';

import { setSmartChoiceSessionStorage } from '../../utils/smartChoice.utils';
import { RecommendationPreferencesData } from '../../utils/smartChoiceEvents';

interface UseSmartChoiceSessionStorageOptions {
  /** The preferences data from the API */
  preferencesData: {
    data?: {
      recommendationPreferencesStatus?: RecommendationPreferencesData;
    };
  } | null;
  /** Whether the current user is a super user */
  isSuperUser: boolean;
  /** Whether the intro popup feature is enabled */
  enableSmartChoiceIntroPopup: boolean;
  /** Whether Smart Choice feature is enabled (used in Routes) */
  isSmartChoice?: boolean;
  /** Whether to wait for a delay before setting sessionStorage (used in Routes) */
  hasDelayPassed?: boolean;
  /** Callback to update recommendation preferences in store */
  setRecommendationPreferences?: (
    preferences: RecommendationPreferencesData
  ) => void;
  setIsSessionStorageReady?: (ready: boolean | undefined) => void;

  /** Disable the hook entirely */
  disabled?: boolean;
}

/**
 * Custom hook to handle Smart Choice sessionStorage initialization
 *
 * This hook:
 * 1. Sets hasDismissedPopover and hasDismissedCoachMarkWeb in sessionStorage
 * 2. Optionally updates the recommendation preferences in the store
 * 3. Returns whether sessionStorage has been initialized
 *
 */
export const useSmartChoiceSessionStorage = ({
  preferencesData,
  isSuperUser,
  enableSmartChoiceIntroPopup,
  isSmartChoice = true,
  hasDelayPassed = true,
  setRecommendationPreferences,
  setIsSessionStorageReady,
  disabled = false,
}: UseSmartChoiceSessionStorageOptions) => {
  useEffect(() => {
    // Early return - when calling it from SmartChoiceWidget but not within parcel.
    if (disabled) return;

    const preferences = preferencesData?.data?.recommendationPreferencesStatus;

    // For Routes: requires isSmartChoice and hasDelayPassed
    // For SmartChoiceWidget: these default to true
    if (isSmartChoice && preferences && hasDelayPassed) {
      setSmartChoiceSessionStorage(preferences, {
        isSuperUser,
        enableSmartChoiceIntroPopup,
      });

      if (setIsSessionStorageReady) {
        setIsSessionStorageReady(true);
      }

      // Optionally update the store (used in Routes)
      if (setRecommendationPreferences) {
        setRecommendationPreferences(preferences);
      }
    }
  }, [
    preferencesData?.data?.recommendationPreferencesStatus,
    isSuperUser,
    enableSmartChoiceIntroPopup,
    isSmartChoice,
    hasDelayPassed,
    setRecommendationPreferences,
    setIsSessionStorageReady,
    disabled,
  ]);
};
